/**
 * Route guard for the operator SPA. Wraps every operator-facing route in
 * `App.tsx`; public driver/viewer routes (`/share/.../viewer`,
 * `/join/<CODE>`, `/event/<id>`, `/login`, `/invite/<token>`) are NOT
 * wrapped — App.tsx unwraps them before reaching it.
 *
 * This is NOT the security boundary. Every API call an operator route makes
 * is independently checked server-side (`services/auth/policy.py::
 * require_authz`, wired across the control plane), so this component is a UX
 * nicety: don't render a broken dashboard, bounce to /login immediately.
 *
 * "Has a session" = `principal.kind === 'user'` — deliberately not
 * `principal.is_superadmin` (an organizer without superadmin still
 * needs the operator routes for their own events) and not
 * `'operator_legacy'` (the backend's break-glass bearer bridge; the
 * SPA itself never presents that credential, so `whoami` never
 * resolves to it for a browser session — see `state/authSession.tsx`).
 */

import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Lock, LogOut } from 'lucide-react';

import { useAuthSession } from '@/state/authSession';

export const RequireSession: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { principal, loading } = useAuthSession();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-base">
        <Lock className="size-6 animate-pulse text-fg-secondary" />
      </div>
    );
  }

  if (!principal || principal.kind !== 'user') {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  return <>{children}</>;
};

export default RequireSession;

/** Sign-out affordance for the Navbar. Renders nothing without a session. */
export const SignOutButton: React.FC<{ className?: string }> = ({ className }) => {
  const { principal, logout } = useAuthSession();
  const navigate = useNavigate();

  if (!principal || principal.kind !== 'user') return null;

  const handleSignOut = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <button
      type="button"
      onClick={() => void handleSignOut()}
      title="Sign out"
      aria-label="Sign out of admin"
      className={
        className ??
        'flex items-center gap-1 whitespace-nowrap rounded px-2 py-1 text-xs text-fg-secondary hover:bg-bg-surface hover:text-fg'
      }
    >
      <LogOut className="size-3.5" />
      Sign out
    </button>
  );
};
